import { useEffect, useRef, useState } from 'react';

// Dynamic week calculation — same logic as StataticsSection
const baseWeek = 644;
const startDate = new Date(2026, 5, 2); // June 2, 2026 (when week was 644)
const now = new Date();

const startDay = startDate.getDay();
const daysToFirstSunday = (7 - startDay) % 7;
const firstSunday = new Date(startDate);
firstSunday.setDate(startDate.getDate() + daysToFirstSunday);

let WEEK_TARGET = baseWeek;
if (now >= firstSunday) {
  const msPerWeek = 7 * 24 * 60 * 60 * 1000;
  const weeksSince = Math.floor((now.getTime() - firstSunday.getTime()) / msPerWeek);
  WEEK_TARGET = baseWeek + 1 + weeksSince;
}

const Trustmeets = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const [count, setCount] = useState(0);
  const [weekCount, setWeekCount] = useState(0);
  const [imgTranslateY, setImgTranslateY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Section intersection observer for content animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => { if (sectionRef.current) observer.unobserve(sectionRef.current); };
  }, []);

  // Years counter animation (target: 12)
  useEffect(() => {
    if (isVisible && count < 12) {
      const timer = setTimeout(() => setCount((prev) => prev + 1), 120);
      return () => clearTimeout(timer);
    }
  }, [isVisible, count]);

  // Weeks counter animation (dynamic target)
  useEffect(() => {
    if (isVisible && weekCount < WEEK_TARGET) {
      const timer = setTimeout(() => setWeekCount((prev) => Math.min(prev + 14, WEEK_TARGET)), 30);
      return () => clearTimeout(timer);
    }
  }, [isVisible, weekCount]);

  // Parallax — disabled on mobile
  useEffect(() => {
    const handleScroll = () => {
      if (!imageRef.current || isMobile) return;
      const rect = imageRef.current.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      const viewportCenter = window.innerHeight / 2;
      const offset = (centerY - viewportCenter) * (1 - 0.7);
      setImgTranslateY(-offset);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  return (
    <>
      <section ref={sectionRef} className="about-section-3 py-20 lg:py-28 relative">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="about-wrapper-3 relative z-10">

            {/* Main Content Grid */}
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">

              {/* Counters — Left */}
              <div className="lg:col-span-4">

                {/* Years + Weeks Counter — side by side */}
                <div className={`transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                  <span className="block text-blue-500 text-sm font-semibold mb-4 uppercase tracking-wider trustmeets-label">
                    Entrepreneurship Journey
                  </span>

                  {/* Numbers row */}
                  <div className="flex items-end gap-4 sm:gap-6">
                    {/* Years */}
                    <div className="flex flex-col items-start">
                      <div className="flex items-start leading-none">
                        <span
                          className="font-bold text-gray-900 trustmeets-counter-heading trustmeets-years-num"
                          style={{
                            lineHeight: '1',
                            fontVariantNumeric: 'tabular-nums',
                            fontFeatureSettings: '"tnum", "lnum"',
                          }}
                        >
                          {count}
                        </span>
                        <sup
                          className="font-bold text-gray-900 trustmeets-counter-heading trustmeets-plus"
                          style={{ fontFeatureSettings: '"lnum"' }}
                        >
                          +
                        </sup>
                      </div>
                      <span className="text-base font-medium text-gray-500 trustmeets-subhead mt-1">Years</span>
                    </div>

                    {/* Divider */}
                    <div className="self-stretch flex items-center pb-7">
                      <div className="w-px bg-gray-300 trustmeets-divider" />
                    </div>

                    {/* Weeks */}
                    <div className="flex flex-col items-start">
                      <div className="flex items-start leading-none">
                        <span
                          className="font-bold text-blue-500 trustmeets-counter-heading trustmeets-weeks-num"
                          style={{
                            lineHeight: '1',
                            fontVariantNumeric: 'tabular-nums',
                            fontFeatureSettings: '"tnum", "lnum"',
                          }}
                        >
                          {weekCount}
                        </span>
                      </div>
                      <span className="text-base font-medium text-gray-500 trustmeets-subhead mt-1">Weeks</span>
                    </div>
                  </div>

                  <h2 className="text-xl lg:text-2xl font-medium text-gray-700 leading-relaxed trustmeets-subhead mt-3">
                    of Entrepreneurial Journey.
                  </h2>
                </div>

              </div>

              {/* Content — Right */}
              <div className="lg:col-span-7 lg:col-start-6">
                <div className={`transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                  <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6 trustmeets-heading">
                    Where Trust Meets Entrepreneurship
                  </h2>
                  <p className="text-gray-600 text-lg leading-relaxed mb-8 trustmeets-body">
                    A platform built on integrity, transparency, and real value creation.
                    For over 12 years, Vibrant Viva has empowered thousands of individuals to build
                    sustainable businesses through quality products, ethical direct selling, and
                    transparent compensation systems.
                  </p>
                  <a
                    href="/about-us"
                    className="inline-flex items-center gap-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 group shadow-lg hover:shadow-xl trustmeets-btn"
                  >
                    <span>About Us</span>
                    <span className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full group-hover:rotate-45 transition-transform duration-300">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H7M17 7V17" />
                      </svg>
                    </span>
                  </a>
                </div>
              </div>
            </div>

            {/* IMAGE — parallax on desktop, fixed-height on mobile */}
            <div className="img-parallax-clip rounded-[50px] shadow-2xl overflow-hidden">
              <img
                ref={imageRef}
                src="/assest/built.webp"
                alt="Team collaboration"
                className="w-full block img-main"
                style={{
                  transform: isMobile ? 'none' : `translate(0px, ${imgTranslateY}px)`,
                  willChange: isMobile ? 'auto' : 'transform',
                }}
                onError={(e) => {
                  e.target.src =
                    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&auto=format&fit=crop';
                }}
              />
            </div>

            {/* Bottom text section */}
            <div className={`mt-16 lg:mt-20 relative z-10 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                <div className="lg:col-span-12 flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-12">
                  <div className="flex-1">
                    <h2 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 leading-tight mb-4 lg:mb-2 trustmeets-heading">
                      Where Trust Meets Growth:<br />
                      The <span className="text-blue-500">VibrantViva</span> Entrepreneurship Journey
                    </h2>
                    <p className="text-gray-600 text-lg lg:text-xl leading-relaxed mb-4 lg:mb-0 trustmeets-body">
                      Empowering entrepreneurship through ethical, transparent, and compliant business practices.
                    </p>
                  </div>
                  <div className="flex-shrink-0 w-full lg:w-auto flex justify-start lg:justify-end">
                    <a
                      href="/statutory"
                      className="group inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold px-6 py-3 rounded-full transition-all duration-500 shadow-xl hover:shadow-2xl hover:scale-105 transform trustmeets-btn"
                    >
                      <span className="text-base">Explore More</span>
                      <span className="relative w-8 h-8 flex items-center justify-center bg-white/20 rounded-full overflow-hidden">
                        <span className="absolute inset-0 bg-white/30 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                        <svg className="w-4 h-4 relative z-10 group-hover:rotate-45 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 17L17 7M17 7H7M17 7V17" />
                        </svg>
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap');

        .about-section-3 {
          position: relative;
          background-color: #fff;
          overflow: hidden;
          font-family: 'Outfit', sans-serif;
        }

        .about-section-3 *, .about-section-3 *::before, .about-section-3 *::after {
          font-family: 'Outfit', sans-serif;
        }

        .trustmeets-heading {
          font-family: 'Cormorant Garamond', serif !important;
          font-weight: 800 !important;
          text-shadow: 0 4px 16px rgba(44, 62, 80, 0.18), 0 2px 0 #fff;
          letter-spacing: 0.04em;
        }
        .trustmeets-counter-heading {
          font-family: 'Cormorant Garamond', serif !important;
          font-weight: 600 !important;
        }
        .trustmeets-subhead  { font-family: 'Outfit', sans-serif !important; font-weight: 400 !important; }
        .trustmeets-label    { font-family: 'Outfit', sans-serif !important; font-weight: 500 !important; }
        .trustmeets-body     { font-family: 'Outfit', sans-serif !important; font-weight: 300 !important; }
        .trustmeets-btn      { font-family: 'Outfit', sans-serif !important; font-weight: 500 !important; }

        /* Counter sizes */
        .trustmeets-years-num { font-size: 260px; }
        .trustmeets-weeks-num { font-size: 90px; }
        .trustmeets-plus      { font-size: 48px; margin-top: 100px; }
        .trustmeets-divider   { height: 150px; }

        @media (max-width: 768px) {
          .trustmeets-years-num { font-size: 160px; }
          .trustmeets-weeks-num { font-size: 70px; }
          .trustmeets-plus      { font-size: 36px; margin-top: 65px; }
          .trustmeets-divider   { height: 110px; }
        }

        @media (max-width: 640px) {
          .trustmeets-years-num { font-size: 110px; }
          .trustmeets-weeks-num { font-size: 52px; }
          .trustmeets-plus      { font-size: 26px; margin-top: 44px; }
          .trustmeets-divider   { height: 80px; }
        }

        @media (max-width: 400px) {
          .trustmeets-years-num { font-size: 90px; }
          .trustmeets-weeks-num { font-size: 44px; }
          .trustmeets-plus      { font-size: 22px; margin-top: 36px; }
          .trustmeets-divider   { height: 68px; }
        }

        /* Image */
        .img-parallax-clip {
          overflow: hidden;
          width: 100%;
        }

        .img-main {
          width: 100%;
          height: auto;
          display: block;
          will-change: transform;
        }

        @media (max-width: 640px) {
          .img-parallax-clip {
            height: 240px;
          }
          .img-main {
            height: 100%;
            width: 100%;
            object-fit: cover;
            object-position: center;
            transform: none !important;
            will-change: auto !important;
          }
        }

        @media (min-width: 641px) and (max-width: 1024px) {
          .img-parallax-clip {
            height: 420px;
          }
          .img-main {
            height: 100%;
            object-fit: cover;
            object-position: center;
          }
        }

        .about-section-3::before {
          content: "";
          position: absolute;
          left: 0; right: 0; bottom: 0;
          width: 100%; height: 100%;
          background-color: #fff;
          top: 52%;
          border-top-left-radius: 16px;
          border-top-right-radius: 16px;
          z-index: 0;
        }

        .about-wrapper-3 { position: relative; }

        @media (max-width: 1024px) { .about-section-3::before { top: 55%; } }
        @media (max-width: 768px)  { .about-section-3::before { top: 60%; } }
        @media (max-width: 640px)  { .about-section-3::before { top: 65%; } }
      `}} />
    </>
  );
};

export default Trustmeets;